<template>
  <div class="vi-shell">

    <!-- Toolbar (hidden when printing) -->
    <div class="vi-toolbar no-print">
      <button class="vi-toolbar-btn" @click="router.push(`/vendor/${companyCode}/dashboard`)">
        ← Back to Dashboard
      </button>
      <div class="vi-toolbar-right">
        <span v-if="inv" class="vi-status-chip" :class="`vi-status--${inv.invoice.status}`">
          {{ statusLabel(inv.invoice.status) }}
        </span>
        <button class="vi-toolbar-btn vi-toolbar-btn--primary" @click="printDoc">
          🖨 Print / Save PDF
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="vi-loading no-print">
      <div class="vi-spinner"></div>
      <p>Loading invoice…</p>
    </div>

    <div v-else-if="error" class="vi-error no-print">
      <p>{{ error }}</p>
      <button @click="router.push(`/vendor/${companyCode}/dashboard`)">Go back</button>
    </div>

    <!-- Invoice document -->
    <div v-else-if="inv" class="vi-document" id="printMe">

      <!-- Header -->
      <div class="vi-doc-header">
        <div class="vi-brand">
          <img v-if="business?.logo" :src="hostName + business.logo" class="vi-logo" alt="Logo" />
          <div v-else class="vi-logo-placeholder">{{ (business?.name || '?').charAt(0) }}</div>
          <div class="vi-brand-text">
            <div class="vi-company-name">{{ business?.name || '—' }}</div>
            <div class="vi-company-sub" v-if="business?.address">{{ business.address }}</div>
            <div class="vi-company-sub" v-if="business?.email">{{ business.email }}</div>
          </div>
        </div>
        <div class="vi-title-block">
          <div class="vi-doc-title">INVOICE</div>
          <table class="vi-meta-table">
            <tr>
              <td class="vmt-label">Invoice No</td>
              <td class="vmt-value mono">{{ inv.invoice.invoice_number }}</td>
            </tr>
            <tr>
              <td class="vmt-label">Invoice Date</td>
              <td class="vmt-value">{{ formatDate(inv.invoice.invoice_date) }}</td>
            </tr>
            <tr v-if="inv.invoice.due_date">
              <td class="vmt-label">Due Date</td>
              <td class="vmt-value">{{ formatDate(inv.invoice.due_date) }}</td>
            </tr>
            <tr>
              <td class="vmt-label">PO Reference</td>
              <td class="vmt-value mono">{{ inv.po?.po_number }}</td>
            </tr>
            <tr>
              <td class="vmt-label">Currency</td>
              <td class="vmt-value">{{ inv.invoice.currency }}</td>
            </tr>
            <tr>
              <td class="vmt-label">Status</td>
              <td class="vmt-value">
                <span class="vi-doc-status" :class="`vi-doc-status--${inv.invoice.status}`">
                  {{ statusLabel(inv.invoice.status) }}
                </span>
              </td>
            </tr>
          </table>
        </div>
      </div>

      <div class="vi-divider"></div>

      <!-- Bill To / From -->
      <div class="vi-two-col">
        <div class="vi-address-block">
          <div class="vi-address-head">FROM (VENDOR)</div>
          <div class="vi-address-name">{{ inv.vendor?.name || '—' }}</div>
          <div class="vi-address-line" v-if="inv.vendor?.email">{{ inv.vendor.email }}</div>
          <div class="vi-address-line" v-if="inv.vendor?.phone">{{ inv.vendor.phone }}</div>
          <div class="vi-address-line" v-if="inv.vendor?.address">{{ inv.vendor.address }}</div>
          <div class="vi-address-line" v-if="inv.vendor?.rc_number">RC: {{ inv.vendor.rc_number }}</div>
        </div>
        <div class="vi-address-block">
          <div class="vi-address-head">BILL TO</div>
          <div class="vi-address-name">{{ business?.name || '—' }}</div>
          <div class="vi-address-line" v-if="business?.email">{{ business.email }}</div>
          <div class="vi-address-line" v-if="business?.phone">{{ business.phone }}</div>
          <div class="vi-address-line" v-if="business?.address">{{ business.address }}</div>
        </div>
        <div class="vi-address-block" v-if="inv.po?.delivery_address">
          <div class="vi-address-head">DELIVER TO</div>
          <div class="vi-address-line" style="white-space:pre-line">{{ inv.po.delivery_address }}</div>
        </div>
      </div>

      <div class="vi-divider vi-divider--dashed"></div>

      <!-- PO title -->
      <div class="vi-section-label" v-if="inv.po?.title">{{ inv.po.title }}</div>

      <!-- Line items -->
      <div class="vi-table-section-head">
        <span class="vi-table-section-title">Line Items</span>
        <span class="vi-badge">{{ inv.po?.lines?.length || 0 }} item{{ (inv.po?.lines?.length || 0) !== 1 ? 's' : '' }}</span>
      </div>
      <div class="vi-table-wrap">
        <table class="vi-table">
          <thead>
            <tr>
              <th class="th-no">#</th>
              <th class="th-desc">Description</th>
              <th class="th-qty th-r">Qty</th>
              <th class="th-uom">UOM</th>
              <th class="th-price th-r">Unit Price</th>
              <th class="th-total th-r">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(line, i) in inv.po?.lines" :key="line.id" :class="i % 2 === 1 ? 'tr-alt' : ''">
              <td class="td-no">{{ i + 1 }}</td>
              <td>
                <div class="td-desc">{{ line.description }}</div>
                <div class="td-spec" v-if="line.specification">{{ line.specification }}</div>
              </td>
              <td class="td-r">{{ line.quantity }}</td>
              <td class="td-dim">{{ line.uom || '—' }}</td>
              <td class="td-r td-money">{{ fmt(line.unit_price, inv.invoice.currency) }}</td>
              <td class="td-r td-money td-bold">{{ fmt(line.total, inv.invoice.currency) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="tf-sub">
              <td colspan="5" class="tf-label">Subtotal</td>
              <td class="tf-val">{{ fmt(inv.invoice.subtotal, inv.invoice.currency) }}</td>
            </tr>
            <tr v-if="inv.invoice.tax_amount" class="tf-sub">
              <td colspan="5" class="tf-label">Tax / VAT</td>
              <td class="tf-val">{{ fmt(inv.invoice.tax_amount, inv.invoice.currency) }}</td>
            </tr>
            <tr class="tf-grand">
              <td colspan="5" class="tf-label tf-label-grand">Total Due</td>
              <td class="tf-val tf-val-grand">{{ fmt(inv.invoice.total, inv.invoice.currency) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Payment terms -->
      <div v-if="inv.po?.payment_terms" class="vi-terms-box">
        <span class="vi-terms-label">Payment Terms:</span>
        <span class="vi-terms-val">{{ inv.po.payment_terms }}</span>
      </div>

      <!-- Notes -->
      <div v-if="inv.invoice.notes" class="vi-notes-box">
        <div class="vi-notes-label">Notes</div>
        <div class="vi-notes-text">{{ inv.invoice.notes }}</div>
      </div>

      <div class="vi-divider vi-divider--dashed"></div>

      <!-- Signature block -->
      <div class="vi-sig-grid">
        <div class="vi-sig-item">
          <div class="vi-sig-line"></div>
          <div class="vi-sig-label">Authorised by (Vendor)</div>
          <div class="vi-sig-name">{{ inv.vendor?.name }}</div>
        </div>
        <div class="vi-sig-item">
          <div class="vi-sig-line"></div>
          <div class="vi-sig-label">Received by (Buyer)</div>
        </div>
      </div>

      <!-- Footer -->
      <div class="vi-footer">
        {{ inv.invoice.invoice_number }} · {{ inv.vendor?.name }} · Generated {{ formatDate(new Date().toISOString()) }}
      </div>
    </div>

  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';

export default {
  name: 'VendorInvoice',
  setup() {
    const route       = useRoute();
    const router      = useRouter();
    const companyCode = route.params.companyCode?.toUpperCase() ?? '';
    const invoiceId   = route.params.invoiceId;
    const hostName    = 'http://127.0.0.1:8000/';

    const loading = ref(true);
    const error   = ref(null);
    const inv     = ref(null);

    const business = computed(() => inv.value?.business ?? null);

    const authHeaders = () => ({
      Authorization: `Bearer ${localStorage.getItem('vendor_token')}`,
    });

    const loadInvoice = async () => {
      loading.value = true;
      error.value   = null;
      try {
        const { data } = await axios.get(`/vendor/${companyCode}/invoices/${invoiceId}`, {
          headers: authHeaders(),
        });
        inv.value = data.data;
      } catch (e) {
        error.value = e.response?.data?.message || 'Failed to load invoice.';
        if (e.response?.status === 401) {
          router.push(`/vendor/${companyCode}/login`);
        }
      } finally {
        loading.value = false;
      }
    };

    const printDoc = () => window.print();

    const statusLabel = (s) => ({
      submitted: 'Submitted', approved: 'Approved', paid: 'Paid',
      rejected: 'Rejected', cancelled: 'Cancelled',
    }[s] ?? s);

    const formatDate = (d) =>
      d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

    const fmt = (amount, currency) => {
      if (amount == null) return '—';
      const sym = { NGN: '₦', GBP: '£', USD: '$', EUR: '€' }[currency] ?? (currency ?? '');
      return sym + Number(amount).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    onMounted(loadInvoice);

    return { companyCode, router, hostName, loading, error, inv, business, printDoc, formatDate, fmt, statusLabel };
  },
};
</script>

<style scoped>
/* ── Shell ── */
.vi-shell { background: #f3f4f6; min-height: 100vh; font-family: 'Helvetica Neue', Arial, sans-serif; }

/* ── Toolbar ── */
.vi-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 32px; background: #fff; border-bottom: 1px solid #e4eaf2;
  position: sticky; top: 0; z-index: 10;
}
.vi-toolbar-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 500;
  cursor: pointer; border: 1.5px solid #dde5f0; background: #fff; color: #374151;
}
.vi-toolbar-btn:hover { background: #f4f7fb; }
.vi-toolbar-btn--primary { background: #004AAD; color: #fff; border-color: #004AAD; }
.vi-toolbar-btn--primary:hover { background: #003b8e; }
.vi-toolbar-right { display: flex; gap: 10px; }

/* ── Loading / Error ── */
.vi-loading { display: flex; flex-direction: column; align-items: center; padding: 80px; gap: 14px; color: #7a90b0; }
.vi-spinner { width: 36px; height: 36px; border: 3px solid #e4eaf2; border-top-color: #004AAD; border-radius: 50%; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.vi-error { padding: 60px; text-align: center; color: #dc2626; }

/* ── Document ── */
.vi-document {
  max-width: 820px; margin: 32px auto; background: #fff;
  padding: 60px 72px; box-shadow: 0 4px 24px rgba(0,0,0,.08);
  font-size: 13px; line-height: 1.5; color: #1a2b45;
}

/* Header */
.vi-doc-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 24px; }
.vi-brand { display: flex; align-items: center; gap: 14px; }
.vi-logo { width: 64px; height: 64px; object-fit: contain; border-radius: 8px; }
.vi-logo-placeholder { width: 64px; height: 64px; background: #004AAD; color: #fff; font-size: 28px; font-weight: 800; border-radius: 8px; display: flex; align-items: center; justify-content: center; }
.vi-company-name { font-size: 18px; font-weight: 700; color: #1a2b45; }
.vi-company-sub { font-size: 11.5px; color: #64748b; margin-top: 2px; }

.vi-title-block { text-align: right; }
.vi-doc-title { font-size: 32px; font-weight: 900; color: #004AAD; letter-spacing: .03em; margin-bottom: 12px; }
.vi-meta-table { margin-left: auto; border-collapse: collapse; }
.vmt-label { font-size: 11px; color: #7a90b0; padding: 3px 12px 3px 0; text-align: right; white-space: nowrap; }
.vmt-value { font-size: 12px; font-weight: 600; color: #1a2b45; padding: 3px 0; text-align: right; }
.mono { font-family: monospace; color: #004AAD; }
.vi-doc-status { display: inline-block; padding: 2px 9px; border-radius: 12px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; }
.vi-doc-status--submitted { background: #fef3c7; color: #92400e; }
.vi-doc-status--approved  { background: #dbeafe; color: #1e40af; }
.vi-doc-status--paid      { background: #d1fae5; color: #064e3b; }
.vi-doc-status--rejected  { background: #fee2e2; color: #991b1b; }
.vi-doc-status--cancelled { background: #f3f4f6; color: #6b7280; }

/* Dividers */
.vi-divider { border: none; border-top: 1.5px solid #e4eaf2; margin: 20px 0; }
.vi-divider--dashed { border-top-style: dashed; }

/* Two-col addresses */
.vi-two-col { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; margin-bottom: 8px; }
.vi-address-block { }
.vi-address-head { font-size: 9.5px; font-weight: 800; color: #7a90b0; text-transform: uppercase; letter-spacing: .1em; margin-bottom: 6px; }
.vi-address-name { font-weight: 700; font-size: 13.5px; color: #1a2b45; margin-bottom: 3px; }
.vi-address-line { font-size: 12px; color: #4b5563; margin-bottom: 1px; }

/* Section label */
.vi-section-label { font-size: 15px; font-weight: 700; color: #1a2b45; margin-bottom: 10px; }

/* Table section header */
.vi-table-section-head { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.vi-table-section-title { font-size: 11px; font-weight: 700; color: #7a90b0; text-transform: uppercase; letter-spacing: .08em; }
.vi-badge { background: #004AAD; color: #fff; border-radius: 12px; padding: 1px 8px; font-size: 10.5px; font-weight: 700; }

/* Table */
.vi-table-wrap { overflow-x: auto; }
.vi-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
.vi-table th { padding: 8px 10px; font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #7a90b0; background: #f8fafc; border-bottom: 2px solid #e4eaf2; text-align: left; }
.vi-table td { padding: 9px 10px; border-bottom: 1px solid #f0f4f8; vertical-align: top; }
.vi-table tr:last-child td { border-bottom: none; }
.th-no { width: 30px; } .th-qty { width: 60px; } .th-uom { width: 50px; } .th-price { width: 100px; } .th-total { width: 110px; }
.th-r, .td-r { text-align: right; }
.td-no { color: #7a90b0; font-size: 12px; }
.td-desc { font-weight: 600; color: #1a2b45; }
.td-spec { font-size: 11px; color: #64748b; margin-top: 2px; }
.td-dim { color: #7a90b0; }
.td-money { font-variant-numeric: tabular-nums; }
.td-bold { font-weight: 700; }
.tr-alt { background: #fafbfc; }

/* Tfoot */
.vi-table tfoot td { padding: 9px 10px; }
.tf-sub td { border-top: 1px solid #e4eaf2; }
.tf-label { color: #64748b; font-size: 12px; text-align: right; padding-right: 10px; }
.tf-val { font-weight: 600; text-align: right; font-variant-numeric: tabular-nums; font-size: 13px; }
.tf-grand { border-top: 2px solid #1a2b45; }
.tf-label-grand { font-weight: 700; color: #1a2b45; font-size: 13px; }
.tf-val-grand { font-weight: 800; color: #004AAD; font-size: 15px; }

/* Terms & notes */
.vi-terms-box { display: flex; gap: 8px; align-items: center; margin-top: 16px; font-size: 12.5px; }
.vi-terms-label { font-weight: 700; color: #374151; }
.vi-terms-val { color: #1a2b45; }
.vi-notes-box { background: #f8fafc; border: 1px solid #e4eaf2; border-radius: 8px; padding: 12px 16px; margin-top: 14px; }
.vi-notes-label { font-size: 10.5px; font-weight: 700; color: #7a90b0; text-transform: uppercase; letter-spacing: .06em; margin-bottom: 4px; }
.vi-notes-text { font-size: 12.5px; color: #374151; white-space: pre-wrap; }

/* Signature */
.vi-sig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-bottom: 28px; }
.vi-sig-line { border-bottom: 1.5px solid #1a2b45; margin-bottom: 8px; height: 40px; }
.vi-sig-label { font-size: 10.5px; font-weight: 700; color: #7a90b0; text-transform: uppercase; letter-spacing: .05em; }
.vi-sig-name { font-size: 12.5px; font-weight: 600; color: #1a2b45; margin-top: 4px; }

/* Footer */
.vi-footer { font-size: 10.5px; color: #aab8cc; text-align: center; margin-top: 16px; }

/* ── Invoice status chip ── */
.vi-status-chip {
  display: inline-flex; align-items: center;
  padding: 4px 12px; border-radius: 20px;
  font-size: 12px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
}
.vi-status--submitted  { background: #fef3c7; color: #92400e; }
.vi-status--approved   { background: #dbeafe; color: #1e40af; }
.vi-status--paid       { background: #d1fae5; color: #064e3b; }
.vi-status--rejected   { background: #fee2e2; color: #991b1b; }
.vi-status--cancelled  { background: #f3f4f6; color: #6b7280; }

/* ── Print styles ── */
@media print {
  .no-print { display: none !important; }
  .vi-shell { background: #fff; }
  .vi-document { margin: 0; padding: 32px 40px; box-shadow: none; max-width: 100%; }
  @page { margin: 12mm 14mm; }
}
</style>
